/**
 * Vite React App Entry Point
 * 
 * Main entry point for the CPU Specification application
 * Sets up React 18 with StrictMode and mounts the App component
 */

/// <reference types="vite/client" />

import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import { AppContextProvider } from './context'
import './index.css'

// Error boundary for development
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Application Error:', error, errorInfo)
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md mx-auto text-center">
            <div className="text-red-600 text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Application Error</h1>
            <p className="text-gray-600 mb-6">
              Something went wrong loading the CPU Specification app.
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <pre className="text-xs text-red-800 text-left overflow-auto">
                {this.state.error?.toString()}
              </pre>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Reload Application
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

// Get root element
const rootElement = document.getElementById('root')

if (!rootElement) {
  throw new Error('Root element not found. Make sure you have a div with id="root" in your HTML.')
}

// Create React root and render app
const root = ReactDOM.createRoot(rootElement)

root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <AppContextProvider>
        <App />
      </AppContextProvider>
    </ErrorBoundary>
  </React.StrictMode>
)

// Hot module replacement for development
if (import.meta.hot) {
  import.meta.hot.accept('./App', () => {
    // Hot reloading App component
  })
}

// Service worker registration (optional, for future PWA features)
if ('serviceWorker' in navigator && import.meta.env?.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then(_registration => {
        // Service worker registered successfully
      })
      .catch(registrationError => {
        console.warn('SW registration failed: ', registrationError)
      })
  })
}

// Performance monitoring (development only)
if (import.meta.env?.DEV) {
  // Track app startup time
  const startTime = performance.now()
  
  // Monitor render performance
  const observer = new PerformanceObserver((list) => {
    const entries = list.getEntries()
    entries.forEach((entry) => {
      if (entry.entryType === 'measure') {
        // console.log(`Performance: ${entry.name} took ${entry.duration.toFixed(2)}ms`)
      }
    })
  })
  
  observer.observe({ entryTypes: ['measure'] })
  
  // Log app startup time when fully loaded
  window.addEventListener('load', () => {
    void (performance.now() - startTime)
  })
}